/**
 * Notification Domain Types
 *
 * Types for the in-app notification system
 */

/**
 * Notification event types
 */
export type NotificationEvent =
  | 'study.assigned'
  | 'study.uploaded'
  | 'study.updated'
  | 'report.created'
  | 'report.signed'
  | 'report.returned'
  | 'report.amended'
  | 'worklist.item_assigned'
  | 'worklist.item_overdue'
  | 'worklist.item_returned'
  | 'user.password_reset'
  | 'user.2fa_enabled'
  | 'user.2fa_disabled'
  | 'system.alert'
  | 'system.maintenance'

/**
 * Notification priority levels
 */
export type NotificationPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT'

/**
 * Notification entity from API
 */
export interface Notification {
  id: string
  event: NotificationEvent
  priority: NotificationPriority
  title: string
  message: string
  resourceType: string | null
  resourceId: string | null
  isRead: boolean
  readAt: string | null
  createdAt: string
}

/**
 * Notifications list response
 */
export interface NotificationsResponse {
  notifications: Notification[]
  total: number
  unreadCount: number
  limit: number
  offset: number
}

/**
 * Unread count response
 */
export interface UnreadCountResponse {
  unreadCount: number
}

/**
 * Mark as read response
 */
export interface MarkAsReadResponse {
  message: string
  notification: {
    id: string
    isRead: boolean
    readAt: string | null
  }
}

/**
 * Mark all as read response
 */
export interface MarkAllAsReadResponse {
  message: string
  count: number
}

/**
 * Delete notification response
 */
export interface DeleteNotificationResponse {
  message: string
}

/**
 * Filters for fetching notifications
 */
export interface NotificationFilters {
  isRead?: boolean
  limit?: number
  offset?: number
}

/**
 * Priority badge colors
 */
export const PRIORITY_COLORS: Record<NotificationPriority, string> = {
  LOW: 'bg-gray-100 text-gray-600',
  NORMAL: 'bg-blue-100 text-blue-600',
  HIGH: 'bg-orange-100 text-orange-600',
  URGENT: 'bg-red-100 text-red-600',
}

/**
 * Event icon mapping
 */
export const EVENT_ICONS: Record<string, string> = {
  'study.': 'FileImage',
  'report.': 'FileText',
  'worklist.': 'ClipboardList',
  'user.': 'User',
  'system.': 'AlertCircle',
}

/**
 * Get resource route from notification
 */
export function getNotificationRoute(notification: Notification): string | null {
  if (!notification.resourceType || !notification.resourceId) {
    return null
  }

  switch (notification.resourceType) {
    case 'study':
      return `/studies/${notification.resourceId}`
    case 'report':
      return `/reports/${notification.resourceId}`
    case 'worklist':
      return `/worklist/${notification.resourceId}`
    case 'patient':
      return `/patients/${notification.resourceId}`
    default:
      return null
  }
}
